import { createApi } from "@reduxjs/toolkit/query/react";
import config from "../../config";
import { prepareHeaders } from "../utils";
import { createSerializedFetchBaseQuery } from "../serializedFetchBaseQuery";
import type {
  FinancialDisclosureCollectionsResponse,
  FinancialDisclosureStatusResponse,
} from "../../../../api/src/users/financial-disclosures/route.js";
import type { RootState } from "../../store";
import { FinancialDisclosureUpsertMutationKind } from "../../../../api/src/types.js";

export type {
  FinancialDisclosureCollectionsResponse,
  FinancialDisclosureStatusResponse,
};
export { FinancialDisclosureUpsertMutationKind };

export interface BasePathParams {
  userId: string;
  docId: string;
}

export type PerTypeApprovalMutationArgs = BasePathParams & {
  id: string;
  currentUserId: string;
};

export interface FinancialDisclosureOptimisticFieldMarker {
  rtkEditId: string;
  updatedAt: string;
}

// Upsert response discriminator. Lives on the cache row as an optional field
// because the server's upsert response is shaped as `{ ...row, mutationKind }`
// and we write that response directly into the cache. Declaring it here makes
// `FinancialDisclosureUpsertResponseForCache<TKey>` a clean subtype of
// `FinancialDisclosureCachedCollectionItem<TKey>`, which removes the need for
// any `Omit`-based stripping (and the cast that came with it) at the cache
// write boundary. No cache reader consumes this field; it's effectively a
// debug breadcrumb on rows that came from an upsert response.
export interface FinancialDisclosureOptimisticCacheFields {
  __optimisticFields?: Partial<
    Record<string, FinancialDisclosureOptimisticFieldMarker>
  >;
  draftKey?: string;
  mutationKind?: FinancialDisclosureUpsertMutationKind;
}

export type FinancialDisclosureCollectionKey =
  keyof FinancialDisclosureCollectionsResponse;
export type FinancialDisclosureUpsertCollectionKey = Exclude<
  FinancialDisclosureCollectionKey,
  "legacyJoint"
>;

interface FinancialDisclosureSystemFields {
  id: string;
  userId: string;
  documentId: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdById: string | null;
  updatedById: string | null;
  partnerApprovedAt: string | null;
  selfApprovedAt: string | null;
}

export type FDSystemFieldName = keyof FinancialDisclosureSystemFields;

// RTK mutation payload: user-editable fields only, all optional.
// Values include `| undefined` for exactOptionalPropertyTypes compatibility
// with FDPatch (which receives Zod .partial() output).
export type FinancialDisclosureUpdatePayload<TItem> = {
  [K in Exclude<keyof TItem, FDSystemFieldName>]?: TItem[K] | null | undefined;
};

// Seed values for every user-editable column on a given collection. Nullable
// columns must be explicitly supplied (typically as `null`); non-nullable ones
// must carry a real value. Threaded as `createDefaults` to
// `runFinancialDisclosureUpsertMutation` so the optimistic-create builder
// produces a cached row that structurally satisfies the response type — no
// `as` cast at the boundary. Adding a new column to Prisma forces every modal
// to seed it, surfacing the gap at compile time.
export type FinancialDisclosureRequiredUserFields<
  TKey extends FinancialDisclosureCollectionKey,
> = {
  [K in keyof FinancialDisclosureCollectionsResponse[TKey][number] as K extends FDSystemFieldName
    ? never
    : K]: FinancialDisclosureCollectionsResponse[TKey][number][K];
};

// Cache form of each collection item, expressed from the same pieces the
// optimistic-create builder produces: required user fields, stamped system
// fields, and optional frontend-only optimistic cache fields.
export type FinancialDisclosureCachedCollectionItem<
  TKey extends FinancialDisclosureCollectionKey,
> = FinancialDisclosureRequiredUserFields<TKey> &
  FinancialDisclosureSystemFields &
  FinancialDisclosureOptimisticCacheFields;

export type CachedFinancialDisclosureCollectionsResponse = {
  [K in FinancialDisclosureCollectionKey]: FinancialDisclosureCachedCollectionItem<K>[];
};

// The merge is structural — it only needs id, optional draftKey, and
// __optimisticFields per item. Carrying the precise collection item type
// through here produces gnarly indexed-access diagnostics under generic TKey;
// instead we bound to a minimal CachedRowLike supertype and rely on call sites
// to pass arrays of the precise per-collection type. The result type is
// `TItem[]`, so the precise per-collection typing is preserved end-to-end.
interface CachedRowLike {
  id: string;
  draftKey?: string;
  __optimisticFields?: Partial<
    Record<string, FinancialDisclosureOptimisticFieldMarker>
  >;
}

export function mergeCollectionWithOptimisticFields<
  TItem extends CachedRowLike,
>(currentItems: TItem[], incomingItems: TItem[]): TItem[] {
  const currentById = new Map(currentItems.map((item) => [item.id, item]));
  const incomingById = new Map(
    incomingItems.map((item) => [item.id, item] as const),
  );
  const mergedItems = incomingItems.map((incomingItem): TItem => {
    const currentItem = currentById.get(incomingItem.id);
    const optimisticFields = currentItem?.__optimisticFields;
    if (!currentItem) {
      return incomingItem;
    }

    const nextItem: TItem = {
      ...incomingItem,
      ...(currentItem.draftKey ? { draftKey: currentItem.draftKey } : {}),
    };

    if (!optimisticFields || Object.keys(optimisticFields).length === 0) {
      return nextItem;
    }

    const nextOptimisticFields: Partial<
      Record<string, FinancialDisclosureOptimisticFieldMarker>
    > = {};

    Object.entries(optimisticFields).forEach(([fieldName, marker]) => {
      if (!marker) {
        return;
      }

      nextOptimisticFields[fieldName] = marker;
      nextItem[fieldName as keyof TItem] =
        currentItem[fieldName as keyof TItem];
    });

    if (Object.keys(nextOptimisticFields).length > 0) {
      nextItem.__optimisticFields = nextOptimisticFields;
    }

    return nextItem;
  });

  currentItems.forEach((currentItem) => {
    if (incomingById.has(currentItem.id)) {
      return;
    }

    const hasOptimisticFields =
      currentItem.__optimisticFields &&
      Object.keys(currentItem.__optimisticFields).length > 0;
    const isDraftOnlyItem =
      currentItem.draftKey && currentItem.id === currentItem.draftKey;
    if (hasOptimisticFields || isDraftOnlyItem) {
      mergedItems.push(currentItem);
    }
  });

  return mergedItems;
}

function mergeFinancialDisclosureCollectionsWithOptimisticFields(
  currentCacheData: CachedFinancialDisclosureCollectionsResponse,
  responseData: CachedFinancialDisclosureCollectionsResponse,
): CachedFinancialDisclosureCollectionsResponse {
  return {
    accountsAndAssets: mergeCollectionWithOptimisticFields(
      currentCacheData.accountsAndAssets,
      responseData.accountsAndAssets,
    ),
    businesses: mergeCollectionWithOptimisticFields(
      currentCacheData.businesses,
      responseData.businesses,
    ),
    childAccounts: mergeCollectionWithOptimisticFields(
      currentCacheData.childAccounts,
      responseData.childAccounts,
    ),
    debts: mergeCollectionWithOptimisticFields(
      currentCacheData.debts,
      responseData.debts,
    ),
    incomes: mergeCollectionWithOptimisticFields(
      currentCacheData.incomes,
      responseData.incomes,
    ),
    inheritancesAndTrusts: mergeCollectionWithOptimisticFields(
      currentCacheData.inheritancesAndTrusts,
      responseData.inheritancesAndTrusts,
    ),
    legacyJoint: mergeCollectionWithOptimisticFields(
      currentCacheData.legacyJoint,
      responseData.legacyJoint,
    ),
    realEstates: mergeCollectionWithOptimisticFields(
      currentCacheData.realEstates,
      responseData.realEstates,
    ),
    vehicles: mergeCollectionWithOptimisticFields(
      currentCacheData.vehicles,
      responseData.vehicles,
    ),
  };
}

// eslint-disable-next-line
const financialDisclosuresTagType = "financialDisclosures" as const;
// eslint-disable-next-line
export const fdStatusTagType = "fdStatus" as const;

export function getFinancialDisclosuresTag(args: BasePathParams) {
  return {
    type: financialDisclosuresTagType,
    id: `${args.userId}:${args.docId}`,
  } as const;
}

export function getFDStatusTag(docId: string) {
  return { type: fdStatusTagType, id: docId } as const;
}

export const financialDisclosuresApi = createApi({
  reducerPath: "financialDisclosuresApi",
  baseQuery: createSerializedFetchBaseQuery({
    baseUrl: config.api,
    prepareHeaders,
  }),
  tagTypes: [financialDisclosuresTagType, fdStatusTagType],
  endpoints: (builder) => ({
    getFinancialDisclosuresByUserId: builder.query<
      CachedFinancialDisclosureCollectionsResponse,
      BasePathParams
    >({
      query: ({ userId, docId }) =>
        `users/${userId}/documents/${docId}/financial-disclosures`,
      merge: (currentCacheData, responseData) => {
        return mergeFinancialDisclosureCollectionsWithOptimisticFields(
          currentCacheData,
          responseData,
        );
      },
      providesTags: (_, __, args) => [getFinancialDisclosuresTag(args)],
    }),
    getFinancialDisclosureStatus: builder.query<
      FinancialDisclosureStatusResponse,
      BasePathParams
    >({
      query: ({ userId, docId }) =>
        `users/${userId}/documents/${docId}/financial-disclosures/status`,
      providesTags: (_, __, { docId }) => [getFDStatusTag(docId)],
    }),
  }),
});

export const {
  useGetFinancialDisclosureStatusQuery,
  useGetFinancialDisclosuresByUserIdQuery,
} = financialDisclosuresApi;

// ---------------------------------------------------------------------------
// Cache helpers (used by mutation helpers + buildFinancialDisclosureListCache)
//
// Both helpers are generic on the literal collection key (TKey). The aggregate
// query is declared with a cache type that already includes __optimisticFields
// and draftKey on every item, so indexing the draft by `collectionKey: TKey`
// yields a precisely-typed sub-array — no overload tricks, no internal casts.
// ---------------------------------------------------------------------------

export function selectFinancialDisclosureCollectionQueryState<
  TKey extends FinancialDisclosureCollectionKey,
>(
  state: RootState,
  args: BasePathParams,
  collectionKey: TKey,
): { data: CachedFinancialDisclosureCollectionsResponse[TKey] | undefined } {
  const aggregateData =
    financialDisclosuresApi.endpoints.getFinancialDisclosuresByUserId.select(
      args,
    )(state).data;

  return {
    data: aggregateData?.[collectionKey],
  };
}

export function updateFinancialDisclosureCollectionQueryData<
  TKey extends FinancialDisclosureCollectionKey,
>(
  args: BasePathParams,
  collectionKey: TKey,
  updater: (draft: CachedFinancialDisclosureCollectionsResponse[TKey]) => void,
) {
  return financialDisclosuresApi.util.updateQueryData(
    "getFinancialDisclosuresByUserId",
    args,
    (draft) => {
      updater(draft[collectionKey]);
    },
  );
}

export function selectFDStatusQueryState(
  state: RootState,
  args: BasePathParams,
): { data?: FinancialDisclosureStatusResponse | undefined } {
  return financialDisclosuresApi.endpoints.getFinancialDisclosureStatus.select(
    args,
  )(state);
}

export function updateFDStatusQueryData(
  args: BasePathParams,
  updater: (draft: FinancialDisclosureStatusResponse) => void,
) {
  return financialDisclosuresApi.util.updateQueryData(
    "getFinancialDisclosureStatus",
    args,
    updater,
  );
}

// ---------------------------------------------------------------------------
// List cache builder — bridges the aggregate query to useFDAutosave's
// listCache param by providing collection-specific select/update accessors.
// ---------------------------------------------------------------------------

interface FinancialDisclosureListCache<
  TKey extends FinancialDisclosureCollectionKey,
> {
  args: BasePathParams;
  data: CachedFinancialDisclosureCollectionsResponse[TKey] | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  selectQueryState: (
    state: RootState,
    queryArgs: BasePathParams,
  ) => {
    data?: CachedFinancialDisclosureCollectionsResponse[TKey] | undefined;
  };
  updateQueryData: (
    queryArgs: BasePathParams,
    updater: (
      draft: CachedFinancialDisclosureCollectionsResponse[TKey],
    ) => void,
  ) => unknown;
}

export function buildFinancialDisclosureListCache<
  TKey extends FinancialDisclosureCollectionKey,
>(params: {
  args: BasePathParams;
  collectionKey: TKey;
  result: {
    data?: CachedFinancialDisclosureCollectionsResponse[TKey] | undefined;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
  };
}): FinancialDisclosureListCache<TKey> {
  const { args, collectionKey, result } = params;

  return {
    args,
    data: result.data,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isError: result.isError,
    selectQueryState: (state: RootState, queryArgs: BasePathParams) =>
      selectFinancialDisclosureCollectionQueryState(
        state,
        queryArgs,
        collectionKey,
      ),
    updateQueryData: (
      queryArgs: BasePathParams,
      updater: (
        draft: CachedFinancialDisclosureCollectionsResponse[TKey],
      ) => void,
    ) =>
      updateFinancialDisclosureCollectionQueryData(
        queryArgs,
        collectionKey,
        updater,
      ),
  };
}
